import React, { useState } from "react";
import { Modal, Upload, Button, UploadFile, Spin, notification } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import { Text } from "components/Text";
import { useTranslation } from "react-i18next";
import { postAllTypeFile } from "services/storageService";
import { PrimaryButton } from "components/SuperAdminAndBusiness/PrimaryButton";

interface UploadEvidenceModalProps {
  open: boolean;
  onClose: () => void;
  selectedFolderKey: string | null;
  reportUuid: string;
  reportIssuesUuid: string;
  onUploadSuccess?: () => void;
}

const UploadEvidenceModal: React.FC<UploadEvidenceModalProps> = ({
  open,
  onClose,
  selectedFolderKey,
  reportUuid,
  reportIssuesUuid,
  onUploadSuccess,
}) => {
  const { t } = useTranslation();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [loading, setLoading] = useState(false);

  const handleChange = ({
    fileList: newFileList,
  }: {
    fileList: UploadFile[];
  }) => {
    setFileList(newFileList);
  };

  const handleRemove = (file: UploadFile) => {
    setFileList((prev) => prev.filter((f) => f.uid !== file.uid));
  };

  const handleUpload = async () => {
    setLoading(true);
    try {
      const uploadPromises = fileList.map((file) => {
        const originalFile = file.originFileObj;
        if (!originalFile) {
          throw new Error("Arquivo inválido");
        }
        return postAllTypeFile(
          originalFile,
          reportIssuesUuid ? "" : reportUuid,
          reportIssuesUuid ? reportIssuesUuid : undefined,
          selectedFolderKey || undefined
        );
      });

      await Promise.all(uploadPromises);
      notification.success({
        message: t("filesUploadedSuccessfully"),
        placement: "bottomRight",
      });
      setFileList([]);
      onClose();
      if (onUploadSuccess) {
        onUploadSuccess();
      }
    } catch (error) {
      notification.error({
        message: t("errorUploadingFiles"),
        placement: "bottomRight",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={480}
      className="max-w-full"
      destroyOnClose
      title={
        <Text className="text-lg md:text-xl font-semibold text-blue_gray-900">
          {t("uploadFiles")}
        </Text>
      }
    >
      <Spin spinning={loading} tip={t("uploading")}>
        <Text className="text-sm md:text-base text-blue_gray-400 block mb-2">
          {t("selectFilesForUpload")}
        </Text>
        <Upload.Dragger
          multiple
          fileList={fileList}
          beforeUpload={() => false}
          onChange={handleChange}
          onRemove={handleRemove}
          className="mb-4"
          listType="picture"
        >
          <p className="ant-upload-drag-icon flex justify-center">
            <InboxOutlined className="text-3xl text-primary" />
          </p>
          <Text className="ant-upload-text text-center text-base">
            {t("dragOrClickToSelect")}
          </Text>
        </Upload.Dragger>
        <div className="flex flex-col md:flex-row gap-2 justify-end mt-4">
          <Button onClick={onClose} size="large" className="min-w-[120px]">
            {t("cancel")}
          </Button>
          <PrimaryButton
            size="large"
            className="min-w-[140px]"
            onClick={handleUpload}
            disabled={fileList.length === 0 || loading}
            loading={loading}
          >
            {t("upload")}
          </PrimaryButton>
        </div>
      </Spin>
    </Modal>
  );
};

export default UploadEvidenceModal;
